import { groupBy, sortBy } from 'lodash';
import React from 'react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SelectionNewVersion } from 'src/domains/sportsbook/webview/components/Selection';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import {
    Empty,
    MarketLoader,
    MarketLoaderWrapper,
    SelectionGroupListVersion,
    SelectionsGroup,
    SelectionsGroupWrap,
} from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified.style';
import { observer } from 'src/utils/mobx-react';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import {
    CorrectScoreRoot,
    HeaderCell,
    SelectionCell,
    SelectionsGroupWrapper,
    SelectionHeader,
} from './CorrectScoreMarketGroup.style';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';

interface PropsType {
    marketId: MarketId;
    templateName: string;
    eventId: EventId;
    market: MarketModel;
    markets?: Array<MarketId>;
    selections: Record<number | string, SelectionModel[]>;
    isOpen?: boolean;
    onClick?(): void;
}

const INITIAL_ROWS_LIMIT = 4;

const getTemplateId = (selection: SelectionModel, event: EventModel): string => {
    const marketTemplateId = selection.templateMarketId;
    // Get market display template and if is 1-3 column then create a single group
    const marketDisplayTemplate = selection.marketDisplayTemplate;
    // Use selection template when no identifier was found
    let templateId = selection.identifier ?? selection.templateId;

    if (templateId === 'custom') {
        const { homeParticipant, awayParticipant } = event;
        const partcipant = selection.name.match(/[a-z]+/i)?.[0] ?? '';

        if (partcipant === homeParticipant) {
            return 'H';
        }

        if (partcipant === awayParticipant) {
            return 'A';
        }

        if (partcipant === 'Draw') {
            return 'D';
        }
    }

    //TODO: temporary solution for anyOtherScore templateId ='??' -> should be fixed on the BE
    templateId = templateId === 'T' || templateId === '??' ? (templateId = 'D') : templateId;
    // Create selections groups based on what display template was specified
    // otherwise fallback to default behavior

    if (templateId === undefined) {
        return '';
    }

    if (marketDisplayTemplate === undefined || marketTemplateId === undefined) {
        return templateId;
    }

    if (marketTemplateId.includes('correct-score')) {
        return templateId.charAt(0);
    } else {
        return templateId;
    }
};

const getOrder = (selection: SelectionModel): [number, number, number] => {
    const templateId = selection.identifier ?? selection.templateId;

    const match = templateId?.match(/^([HDA])(\d+)-(\d+)$/);

    if (!match) {
        return [999, 999, 999];
    }

    const [, type, first, second] = match;

    const score1 = Number(first);
    const score2 = Number(second);

    const group = type === 'H' ? 0 : type === 'D' ? 1 : 2;

    if (type === 'A') {
        return [group, score2, score1];
    }

    return [group, score1, score2];
};

const groupSelections = (selections: Array<SelectionModel>, event: EventModel) => {
    const selectionsArrayNew = sortBy(selections, (selection) => getOrder(selection));

    return groupBy(selectionsArrayNew, (selection) => getTemplateId(selection, event));
};

const getHeaderLabel = (
    key: string,
    homeParticipant: string | null,
    awayParticipant: string | null
): React.ReactNode => {
    switch (key) {
        case 'H':
            return homeParticipant;
        case 'D':
            return (
                <I18n
                    langKey='events.correct-score-market.draw'
                    defaultText='Draw'
                />
            );
        case 'A':
            return awayParticipant;
        default:
            return null;
    }
};

const selectionsByPropsNew = (selections: Record<string, SelectionModel[]>): SelectionModel[] => {
    const selectionsList: Record<number, SelectionModel[]> = {};

    for (const key of Object.keys(selections)) {
        const singleSelection = selections[key] ?? [];
        //@ts-expect-error
        selectionsList[key] = singleSelection.map((selection: SelectionModel | null) => {
            //eslint-disable-next-line
            return (selection = selection && selection.id2.getModel());
        });
    }

    const selectionsListValues = Object.values(selectionsList);
    return selectionsListValues[0] ?? [];
};

export const CorrectScoreMarketGroup = observer('CorrectScoreMarketGroup', (props: PropsType) => {
    const { marketId, templateName, selections, isOpen = true, onClick } = props;

    return (
        <CorrectScoreRoot className='market-group--correct-score'>
            {typeof onClick === 'function' ? (
                <MarketHeader
                    name={templateName}
                    isOpen={isOpen}
                    onClick={onClick}
                    marketId={marketId}
                />
            ) : null}
            {isOpen ? (
                <MarketBody
                    marketId={marketId}
                    selections={selections}
                />
            ) : null}
        </CorrectScoreRoot>
    );
});

const MarketBody = observer(
    'MarketBody',
    (props: { marketId: MarketId; selections: Record<string | string, SelectionModel[]> }) => {
        const { marketId } = props;
        const [isExpanded, setIsExpanded] = React.useState(false);
        const marketModel = marketId.getModel();
        if (marketModel === null) {
            return null;
        }

        const eventModel = marketModel.eventModel;

        if (eventModel === null) {
            return null;
        }

        const { homeParticipant, awayParticipant } = eventModel;

        const selections = selectionsByPropsNew(props.selections);
        const newAllSelectionsProps = groupSelections(selections, eventModel);
        const selectionSize = selections.length;
        const selectionsToRender = Object.entries(newAllSelectionsProps);
        const columns = selectionsToRender.length;
        const limitDisplayBasedOnColumns = selectionSize / columns;
        const eventMarket = marketId.getModel();
        const maxLength = Math.max.apply(
            null,
            selectionsToRender.map(([_, items]) => items.length)
        );

        const toggleExpand = (e: React.MouseEvent): void => {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
        };

        return (
            <>
                <SelectionHeader>
                    {selectionsToRender.map(([key]) => (
                        <HeaderCell key={key}>{getHeaderLabel(key, homeParticipant, awayParticipant)}</HeaderCell>
                    ))}
                </SelectionHeader>
                <SelectionsGroupWrap>
                    <SelectionsGroup className='selections-group'>
                        <Empty
                            className='empty'
                            selectionsCount={columns}
                        >
                            {columns === 0 ? (
                                <MarketLoaderWrapper>
                                    {eventMarket === null ? <MarketLoader version='dark' /> : null}
                                </MarketLoaderWrapper>
                            ) : (
                                <>
                                    <SelectionsGroupWrapper>
                                        {selectionsToRender.map(([key, itemsIn]: [string, SelectionModel[]]) => {
                                            let itemsAligned: (SelectionModel | null)[] = [...itemsIn];
                                            const selection = itemsIn[0];

                                            if (itemsAligned.length < maxLength) {
                                                itemsAligned.push(
                                                    ...new Array(maxLength - itemsAligned.length).fill(null)
                                                );
                                            }

                                            if (selection !== undefined) {
                                                const columnRows =
                                                    eventMarket !== null && isExpanded
                                                        ? selectionSize
                                                        : INITIAL_ROWS_LIMIT;
                                                const selectionsToRender = isExpanded
                                                    ? itemsAligned
                                                    : itemsAligned.slice(0, columnRows);

                                                return (
                                                    <SelectionGroupListVersion
                                                        key={key}
                                                        className='selections-group-list'
                                                        columns={1}
                                                    >
                                                        {selectionsToRender.map((item, i) => {
                                                            if (item === null) {
                                                                return <SelectionCell key={`placeholder-${i}`} />;
                                                            }

                                                            const score =
                                                                (item.templateId || item.name)
                                                                    ?.match(/\d+/g)
                                                                    ?.join('-') ?? '';
                                                            const lineLabel = isNaN(parseInt(score))
                                                                ? item.name.toLowerCase().includes('any other')
                                                                    ? 'Any other score'
                                                                    : item.name
                                                                : score;

                                                            return (
                                                                <SelectionCell key={item.name}>
                                                                    <SelectionNewVersion
                                                                        selectionId={item.id2}
                                                                        sp={false}
                                                                        lineLabel={lineLabel}
                                                                    />
                                                                </SelectionCell>
                                                            );
                                                        })}
                                                    </SelectionGroupListVersion>
                                                );
                                            }
                                        })}
                                    </SelectionsGroupWrapper>
                                </>
                            )}
                        </Empty>
                    </SelectionsGroup>
                    {limitDisplayBasedOnColumns > INITIAL_ROWS_LIMIT ? (
                        <ExpandButton
                            isExpanded={isExpanded}
                            toggleExpand={toggleExpand}
                            isHomepage={false}
                        />
                    ) : undefined}
                </SelectionsGroupWrap>
            </>
        );
    }
);
